import React, { useRef, useState } from 'react';
import axios from 'axios';
import { isValidAbn, spaceAbnInput } from '../businessNumber';
import Field from './SiteField';

// The website's Trade Enquiry (issue #286): what every "Become a stockist"
// button opens. POST /api/site/trade-enquiry (tradeEnquiryService.js) does
// the matching, the Lead and the staff to-do; this page only asks.
//
// Never calls itself an application: the Trading Account Application is
// the separate legal document a qualified business is sent later.

const AU_STATES = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'];
const NZ_REGIONS = [
  'Northland', 'Auckland', 'Waikato', 'Bay of Plenty', 'Gisborne', "Hawke's Bay", 'Taranaki',
  'Manawatū-Whanganui', 'Wellington', 'Tasman', 'Nelson', 'Marlborough', 'West Coast',
  'Canterbury', 'Otago', 'Southland',
];
const BUSINESS_TYPES = [
  { value: 'retailer', label: 'Retailer' },
  { value: 'installer', label: 'Installer' },
  { value: 'other', label: 'Other' },
];

const EMPTY = {
  country: 'AU', business_name: '', contact_name: '', email: '', phone: '', business_type: '',
  abn: '', state: '', postcode: '', region: '', nzbn: '', message: '', website: '',
};

function TradeEnquiryPage({ go }) {
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const openedAt = useRef(Date.now());
  const isAu = form.country === 'AU';

  const set = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    if (isAu && !isValidAbn(form.abn)) {
      setError("That ABN doesn't look right. Please check it.");
      return;
    }
    setSending(true);
    try {
      await axios.post('/api/site/trade-enquiry', { ...form, form_opened_at: openedAt.current });
      setSent(true);
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err.response?.data?.message || "Sorry, we couldn't send your enquiry. Please try again.");
    }
    setSending(false);
  };

  if (sent) {
    return (
      <section className="ks-section">
        <div className="ks-wrap" style={{ maxWidth: 720 }}>
          <p className="ks-eyebrow">Trade enquiry</p>
          <h1 className="ks-display">Thanks, we&apos;ve got it.</h1>
          <p className="ks-lead">
            We&apos;ll be in touch soon about stocking our range. A confirmation is on its way to {form.email}.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="ks-section ks-section-tight">
      <div className="ks-wrap" style={{ maxWidth: 720 }}>
        <p className="ks-eyebrow">Trade enquiry</p>
        <h1 className="ks-display">Become a stockist.</h1>
        <p className="ks-lead">
          Tell us about your business and we&apos;ll be in touch about stocking our range.
        </p>

        <form className="ks-form" onSubmit={submit}>
          {/* Hidden from people; a bot that fills it in is quietly ignored. */}
          <div className="ks-form-trap" aria-hidden="true">
            <label htmlFor="te-website">Website</label>
            <input id="te-website" name="website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set('website')} />
          </div>

          <Field label="Country" htmlFor="te-country">
            <select id="te-country" value={form.country} onChange={set('country')} required>
              <option value="AU">Australia</option>
              <option value="NZ">New Zealand</option>
            </select>
          </Field>
          <Field label="Business name" htmlFor="te-business">
            <input id="te-business" value={form.business_name} onChange={set('business_name')} maxLength={150} required autoComplete="organization" />
          </Field>
          {isAu ? (
            <>
              <Field label="ABN" htmlFor="te-abn" hint={<>No ABN? <a href="/contact" onClick={go('/contact')}>Contact us</a> instead.</>}>
                <input id="te-abn" value={form.abn} onChange={(e) => setForm((prev) => ({ ...prev, abn: spaceAbnInput(e.target.value) }))} maxLength={14} required inputMode="numeric" placeholder="e.g. 12 345 678 901" />
              </Field>
              <div className="ks-form-row">
                <Field label="State" htmlFor="te-state">
                  <select id="te-state" value={form.state} onChange={set('state')} required>
                    <option value="">Choose…</option>
                    {AU_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="Postcode" htmlFor="te-postcode">
                  <input id="te-postcode" value={form.postcode} onChange={set('postcode')} maxLength={4} required inputMode="numeric" pattern="[0-9]{4}" autoComplete="postal-code" />
                </Field>
              </div>
            </>
          ) : (
            <>
              <Field label="Region" htmlFor="te-region">
                <select id="te-region" value={form.region} onChange={set('region')} required>
                  <option value="">Choose…</option>
                  {NZ_REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </Field>
              <Field label="NZBN (optional)" htmlFor="te-nzbn">
                <input id="te-nzbn" value={form.nzbn} onChange={set('nzbn')} maxLength={16} inputMode="numeric" />
              </Field>
            </>
          )}
          <Field label="Business type" htmlFor="te-type">
            <select id="te-type" value={form.business_type} onChange={set('business_type')} required>
              <option value="">Choose…</option>
              {BUSINESS_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Your name" htmlFor="te-name">
            <input id="te-name" value={form.contact_name} onChange={set('contact_name')} maxLength={150} required autoComplete="name" />
          </Field>
          <div className="ks-form-row">
            <Field label="Email" htmlFor="te-email" hint="Your business email address, if you have one.">
              <input id="te-email" type="email" value={form.email} onChange={set('email')} maxLength={255} required autoComplete="email" />
            </Field>
            <Field label="Phone" htmlFor="te-phone">
              <input id="te-phone" type="tel" value={form.phone} onChange={set('phone')} maxLength={30} required autoComplete="tel" />
            </Field>
          </div>
          <Field label="Message (optional)" htmlFor="te-message">
            <textarea id="te-message" value={form.message} onChange={set('message')} maxLength={2000} rows={5} placeholder="The brands you're interested in, where you sell, anything else we should know." />
          </Field>

          {error && <p className="ks-form-error" role="alert">{error}</p>}
          <div className="ks-btn-row" style={{ marginTop: 8 }}>
            <button type="submit" className="ks-btn ks-btn-solid" disabled={sending}>
              {sending ? 'Sending…' : 'Send enquiry'}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

export default TradeEnquiryPage;
